import { apiFetch } from '@/lib/api';
import type {
  CvmAppeals,
  CvmCaseDetail,
  CvmCaseList,
  CvmConsistency,
  CvmDecisionSearch,
  CvmDirector,
  CvmDirectors,
  CvmLedger,
  CvmOverview,
  CvmParty,
  CvmPartyList,
  CvmPipeline,
  CvmSanctions,
  CvmSessionList,
  CvmSettlements,
  CvmVotes,
} from '@/services/types';

/** The sixth agency. Same envelope, same substrate contract; parties named as published. */
export function fetchCvmOverview(): Promise<CvmOverview> {
  return apiFetch<CvmOverview>('/api/v1/cvm/overview');
}

export interface CvmCaseQuery {
  /** Process number digits, an accused's name or a word of the ementa. */
  q?: string | null;
  year?: number | null;
  rite?: string | null;
  unit?: string | null;
  /** An item outcome code (CONDEMNED, ACQUITTED, ...). */
  outcome?: string | null;
  sanction_type?: string | null;
  /** The relator's canonical name, as the filter options list it (not the slug). */
  relator?: string | null;
  limit?: number;
  offset?: number;
}

/**
 * The filter params, without the page bounds, so the CSV export replays the
 * SCREEN's exact query (`limit`/`offset` describe a page, never a result).
 */
export function cvmCaseParams(query: CvmCaseQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.q) params.set('q', query.q);
  if (query.year) params.set('year', String(query.year));
  if (query.rite) params.set('rite', query.rite);
  if (query.unit) params.set('unit', query.unit);
  if (query.outcome) params.set('outcome', query.outcome);
  if (query.sanction_type) params.set('sanction_type', query.sanction_type);
  if (query.relator) params.set('relator', query.relator);
  return params;
}

export function fetchCvmCases(query: CvmCaseQuery): Promise<CvmCaseList> {
  const params = cvmCaseParams(query);
  params.set('limit', String(query.limit ?? 50));
  params.set('offset', String(query.offset ?? 0));
  return apiFetch<CvmCaseList>(`/api/v1/cvm/cases?${params.toString()}`);
}

/** One judged case. `key` is the 17-digit NUP or the legacy `RJ2010/8588` / `IA18/2008`. */
export function fetchCvmCase(key: string): Promise<CvmCaseDetail> {
  return apiFetch<CvmCaseDetail>(`/api/v1/cvm/cases/${encodeURIComponent(key)}`);
}

export interface CvmDecisionQuery {
  q?: string | null;
  /** `extrato` | `relatorio` | `voto`: which text segment to search. */
  segment?: string | null;
  year?: number | null;
  /** The relator's canonical name, exact. */
  relator?: string | null;
  limit?: number;
  offset?: number;
}

export function cvmDecisionParams(query: CvmDecisionQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.q) params.set('q', query.q);
  if (query.segment) params.set('segment', query.segment);
  if (query.year) params.set('year', String(query.year));
  if (query.relator) params.set('relator', query.relator);
  return params;
}

export function fetchCvmDecisions(query: CvmDecisionQuery): Promise<CvmDecisionSearch> {
  const params = cvmDecisionParams(query);
  params.set('limit', String(query.limit ?? 25));
  params.set('offset', String(query.offset ?? 0));
  return apiFetch<CvmDecisionSearch>(`/api/v1/cvm/decisions?${params.toString()}`);
}

export function fetchCvmSanctions(): Promise<CvmSanctions> {
  return apiFetch<CvmSanctions>('/api/v1/cvm/sanctions');
}

export interface CvmLedgerQuery {
  sanction_type?: string | null;
  /** The normalised violated provision, as the sanctions screen lists it. */
  provision?: string | null;
  year?: number | null;
  /** A substring of the party's canonical name (never a slug). */
  party?: string | null;
  /** Only rows the parser attributed to a named accused. */
  attributed_only?: boolean | null;
  limit?: number;
  offset?: number;
}

export function cvmLedgerParams(query: CvmLedgerQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.sanction_type) params.set('sanction_type', query.sanction_type);
  if (query.provision) params.set('provision', query.provision);
  if (query.year) params.set('year', String(query.year));
  if (query.party) params.set('party', query.party);
  if (query.attributed_only) params.set('attributed_only', 'true');
  return params;
}

export function fetchCvmLedger(query: CvmLedgerQuery): Promise<CvmLedger> {
  const params = cvmLedgerParams(query);
  params.set('limit', String(query.limit ?? 50));
  params.set('offset', String(query.offset ?? 0));
  return apiFetch<CvmLedger>(`/api/v1/cvm/sanctions/ledger?${params.toString()}`);
}

export interface CvmPageQuery {
  limit?: number;
  offset?: number;
}

export function fetchCvmVotes(query: CvmPageQuery): Promise<CvmVotes> {
  const params = new URLSearchParams();
  params.set('limit', String(query.limit ?? 50));
  params.set('offset', String(query.offset ?? 0));
  return apiFetch<CvmVotes>(`/api/v1/cvm/votes?${params.toString()}`);
}

export function fetchCvmDirectors(): Promise<CvmDirectors> {
  return apiFetch<CvmDirectors>('/api/v1/cvm/directors');
}

/** One director, by the slug of the canonical name. */
export function fetchCvmDirector(slug: string): Promise<CvmDirector> {
  return apiFetch<CvmDirector>(`/api/v1/cvm/r/${encodeURIComponent(slug)}`);
}

export interface CvmSessionQuery {
  /** `JULGAMENTO` | `COLEGIADO` | `CRSFN`. */
  kind?: string | null;
  limit?: number;
  offset?: number;
}

export function cvmSessionParams(query: CvmSessionQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.kind) params.set('kind', query.kind);
  return params;
}

export function fetchCvmSessions(query: CvmSessionQuery): Promise<CvmSessionList> {
  const params = cvmSessionParams(query);
  params.set('limit', String(query.limit ?? 50));
  params.set('offset', String(query.offset ?? 0));
  return apiFetch<CvmSessionList>(`/api/v1/cvm/sessions?${params.toString()}`);
}

export interface CvmPartyQuery {
  q?: string | null;
  /** `person` | `company` | `unknown`. */
  entity_type?: string | null;
  limit?: number;
  offset?: number;
}

export function cvmPartyParams(query: CvmPartyQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.q) params.set('q', query.q);
  if (query.entity_type) params.set('entity_type', query.entity_type);
  return params;
}

export function fetchCvmParties(query: CvmPartyQuery): Promise<CvmPartyList> {
  const params = cvmPartyParams(query);
  params.set('limit', String(query.limit ?? 50));
  params.set('offset', String(query.offset ?? 0));
  return apiFetch<CvmPartyList>(`/api/v1/cvm/parties?${params.toString()}`);
}

/** One party RECORD (never a profile): what the documents say under this name. */
export function fetchCvmParty(slug: string): Promise<CvmParty> {
  return apiFetch<CvmParty>(`/api/v1/cvm/parties/${encodeURIComponent(slug)}`);
}

export interface CvmSettlementQuery {
  /** `ACCEPTED` | `REJECTED`. */
  outcome?: string | null;
  year?: number | null;
  q?: string | null;
  limit?: number;
  offset?: number;
}

export function cvmSettlementParams(query: CvmSettlementQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.outcome) params.set('outcome', query.outcome);
  if (query.year) params.set('year', String(query.year));
  if (query.q) params.set('q', query.q);
  return params;
}

export function fetchCvmSettlements(query: CvmSettlementQuery): Promise<CvmSettlements> {
  const params = cvmSettlementParams(query);
  params.set('limit', String(query.limit ?? 50));
  params.set('offset', String(query.offset ?? 0));
  return apiFetch<CvmSettlements>(`/api/v1/cvm/settlements?${params.toString()}`);
}

export interface CvmAppealQuery {
  /** `DENIED` | `GRANTED` | `PARTIAL` | `NOT_ADMITTED`. */
  outcome?: string | null;
  q?: string | null;
  limit?: number;
  offset?: number;
}

export function cvmAppealParams(query: CvmAppealQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.outcome) params.set('outcome', query.outcome);
  if (query.q) params.set('q', query.q);
  return params;
}

export function fetchCvmAppeals(query: CvmAppealQuery): Promise<CvmAppeals> {
  const params = cvmAppealParams(query);
  params.set('limit', String(query.limit ?? 50));
  params.set('offset', String(query.offset ?? 0));
  return apiFetch<CvmAppeals>(`/api/v1/cvm/appeals?${params.toString()}`);
}

export interface CvmPipelineQuery {
  /** The register's current phase, verbatim. */
  phase?: string | null;
  q?: string | null;
  limit?: number;
  offset?: number;
}

export function cvmPipelineParams(query: CvmPipelineQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.phase) params.set('phase', query.phase);
  if (query.q) params.set('q', query.q);
  return params;
}

export function fetchCvmPipeline(query: CvmPipelineQuery): Promise<CvmPipeline> {
  const params = cvmPipelineParams(query);
  params.set('limit', String(query.limit ?? 50));
  params.set('offset', String(query.offset ?? 0));
  return apiFetch<CvmPipeline>(`/api/v1/cvm/pipeline?${params.toString()}`);
}

export interface CvmConsistencyQuery {
  provision?: string | null;
  limit?: number;
  offset?: number;
}

export function cvmConsistencyParams(query: CvmConsistencyQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.provision) params.set('provision', query.provision);
  return params;
}

export function fetchCvmConsistency(query: CvmConsistencyQuery): Promise<CvmConsistency> {
  const params = cvmConsistencyParams(query);
  params.set('limit', String(query.limit ?? 50));
  params.set('offset', String(query.offset ?? 0));
  return apiFetch<CvmConsistency>(`/api/v1/cvm/consistency?${params.toString()}`);
}
